import { Alert } from '~components/web-components'
import { useEffect, useMemo } from 'react'
import TagManager from 'react-gtm-module'
import { Helmet } from 'react-helmet-async'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import { Splash } from '~components/page-header/splash'
import { useIsActiveUser } from '~hooks/permissions/use-is-active-user'
import { useConstructLinkWithPool } from '~hooks/use-construct-link-with-pool'
import { usePowerBiReport } from '../../../hooks/use-power-bi-report'
import { CiiPredictiveToolReport } from './components/cii-predictive-tool-report'
import { EuEtsReport } from './components/eu-ets-report'
import { FuelEUReport } from './components/fuel-eu-maritime-report'
import { ReportError } from './components/report-error'
import {
  EmissionsReportTypes,
  findEmissionReportType,
  parseReportTypeParam,
} from './utils'
import { PageWrapper } from '~components/page-wrapper'
import { ExactScopeName, useHasScope } from '~hooks/permissions/use-has-scope'
import { exhaustiveCheck } from '../financials/earnings/utils/format-date-by-aggregate'

export default function EmissionPageWrapper() {
  const { reportType } = useParams()
  const menuItems = useEmissionsMenuLinks()
  const location = useLocation()
  const homeLink = useConstructLinkWithPool(
    '/emissions/:pool/cii-predictive-tool-insite-digital',
  )

  const pageName = useMemo(
    () =>
      menuItems.find(
        (item) => item.link === decodeURIComponent(location.pathname),
      )?.title || '',
    [menuItems, location.pathname],
  )

  if (!reportType) {
    return <Navigate to={homeLink} />
  }

  return (
    <>
      <Helmet>
        <title>InSite | Emission</title>
      </Helmet>
      <Splash pageName={pageName} />
      <PageWrapper>
        <Emission />
      </PageWrapper>
    </>
  )
}

function Emission() {
  const { reportType: reportTypeParam, pool } = useParams()
  const parsedReportType = parseReportTypeParam(reportTypeParam)
  const { data, isLoading, isError } = usePowerBiReport(parsedReportType)
  const reportType = findEmissionReportType(reportTypeParam)
  const { isActiveUser } = useIsActiveUser(pool)

  useEffect(() => {
    if (!reportType) return

    const tagManagerArgs = {
      dataLayer: {
        event: 'page_view',
        virtual_page_title: reportType,
        virtual_page_url: location.pathname,
        pool_name: pool,
      },
    }

    TagManager.dataLayer(tagManagerArgs)
  }, [pool, reportType])

  if (isLoading) {
    return <CircularProgress aria-label="Loading…" />
  }

  if (isError || !data || !reportType) {
    return <ReportError />
  }

  if (!isActiveUser) {
    return (
      <Alert severity="info">
        You don't have any active vessels in this pool.
      </Alert>
    )
  }

  switch (reportType) {
    case EmissionsReportTypes.CII:
      return <CiiPredictiveToolReport selectedReport={data} />
    case EmissionsReportTypes.EUETS:
    case EmissionsReportTypes.EUETSInternal:
      return <EuEtsReport selectedReport={data} />
    case EmissionsReportTypes.FuelEU:
    case EmissionsReportTypes.FuelEUInternal:
      return <FuelEUReport selectedReport={data} />
    default:
      return exhaustiveCheck(reportType)
  }
}

export function useEmissionsMenuLinks() {
  const isMtUser = useHasScope(ExactScopeName.maerskTankersUser)

  const ciiLink = useConstructLinkWithPool(
    '/emissions/:pool/cii-predictive-tool-insite-digital',
  )
  const euEtsLink = useConstructLinkWithPool('/emissions/:pool/euets-report')

  const euEtsLinkInternal = useConstructLinkWithPool(
    '/emissions/:pool/euets-report-internal',
  )
  const fuelEuLink = useConstructLinkWithPool('/emissions/:pool/fueleu-report')

  const links = [
    {
      title: 'Carbon Intensity Indicator (CII)',
      link: ciiLink,
    },
    { title: 'EU ETS Allowances', link: euEtsLink },
    {
      title: 'Fuel EU Maritime',
      link: fuelEuLink,
    },
  ]

  if (isMtUser) {
    links.splice(2, 0, {
      title: 'EU ETS Allowances Inventory',
      link: euEtsLinkInternal,
    })
  }

  return links
}
